import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Equipment from './equipment/Equipment';
import Layout from '../../components/Layout';
import { useAuth } from '../../hooks/useAuth';
import AbilityScores from './abilities/AbilityScores';
import SpellSlots from './SpellSlots';
import Feats, { Feat } from './Feats';
import CombatStat from './CombatStat';
import HitPoints, { emptyHealth, Health } from './HitPoints';
import { emptyProficiencies, ProficiencyData } from './Proficiencies';
import './CharacterSheet.css';

type Character = {
    id: number;
    name: string;
    health: Health;
    proficiencies: ProficiencyData;
    proficiencyBonus?: number | null;
    heroicInspiration?: boolean;
    combat: { ac: number; speed: number; initiative: number };
    feats: Feat[];
    weapons: { id: number; source: number; quantity: number; notes: string | null; weight: number }[];
    items: { id: number; item_id: number | null; name: string; quantity: number; weight: number; notes: string | null; value?: { cp: number; sp: number; ep: number; gp: number; pp: number } }[];
    currency: { cp: number; sp: number; ep: number; gp: number; pp: number };
    treasure: { id: number; treasure_id: null; name: string; quantity: number; weight: number; notes: string | null; value?: { cp: number; sp: number; ep: number; gp: number; pp: number } }[];
    abilities: { level: number; scores: Record<string, number>; proficiencies: Record<string, 0 | 1 | 2> };
};

type CharacterResponse = { success: boolean; data: Omit<Character, 'combat' | 'feats' | 'health' | 'proficiencies'> & { combat?: Partial<Character['combat']> | null; feats?: Feat[]; health?: Partial<Health>; proficiencies?: Partial<ProficiencyData> } };

function CharacterSheet() {
    const { characterId } = useParams();
    const { apiRequest } = useAuth();
    const [character, setCharacter] = useState<Character | null>(null);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('main');

    useEffect(() => {
        if (!characterId) return;
        apiRequest<CharacterResponse>(`/api/characters/${characterId}`)
            .then(({ data }) => setCharacter({
                ...data,
                health: { ...emptyHealth, ...data.health },
                proficiencies: { ...emptyProficiencies, ...data.proficiencies },
                feats: data.feats ?? [],
                combat: {
                    ac: data.combat?.ac ?? 10,
                    speed: data.combat?.speed ?? 30,
                    initiative: data.combat?.initiative ?? 0,
                },
            }))
            .catch(() => setError('Could not load this character.'));
    }, [apiRequest, characterId]);

    if (error) return <Layout><div className="container mt-4"><p>{error}</p><Link to="/">Back to characters</Link></div></Layout>;
    if (!character) return <Layout><div className="container mt-4">Loading character…</div></Layout>;

    return (
        <Layout>
            <main className="character-sheet">
                <header className="character-hero">
                    <div className="character-hero__identity">
                        <span className="hero-eyebrow">Your adventurer's chronicle</span>
                        <h1>{character.name} <i className="fa-solid fa-leaf" aria-hidden="true" /></h1>
                        <p>Character sheet</p>
                        <div className="hero-level">Level {character.abilities.level || 1}</div>
                        <Link to="/" className="hero-back"><i className="fa-solid fa-arrow-left" aria-hidden="true" /> All characters</Link>
                    </div>
                </header>

                <div className="character-sheet-tab-content">
                    {activeTab === 'main' && (
                        <div role="tabpanel" aria-label="General stats">
                            <div className="combat-stats">
                                <div className="stat-card"><i className="fa-solid fa-shield-halved" aria-hidden="true" /><CombatStat characterId={character.id} field="ac" label="Armor Class" value={character.combat.ac} onChange={ac => setCharacter(current => current && ({ ...current, combat: { ...current.combat, ac } }))} /></div>
                                <div className="stat-card stat-card--health"><HitPoints characterId={character.id} value={character.health} onChange={health => setCharacter(current => current && ({ ...current, health }))} /></div>
                                <div className="stat-card"><i className="fa-solid fa-shoe-prints" aria-hidden="true" /><CombatStat characterId={character.id} field="speed" label="Speed" value={character.combat.speed} onChange={speed => setCharacter(current => current && ({ ...current, combat: { ...current.combat, speed } }))} /></div>
                                <div className="stat-card"><i className="fa-solid fa-bolt" aria-hidden="true" /><CombatStat characterId={character.id} field="initiative" label="Initiative" value={character.combat.initiative} onChange={initiative => setCharacter(current => current && ({ ...current, combat: { ...current.combat, initiative } }))} /></div>
                            </div>
                            <AbilityScores characterId={character.id} data={character.abilities} level={character.abilities.level || 1} onChange={(abilities) => setCharacter(current => current && ({ ...current, abilities }))}
                                proficiencies={character.proficiencies} onProficiencyChange={(field, value) => setCharacter(current => current && ({ ...current, proficiencies: { ...current.proficiencies, [field]: value } }))}
                                proficiencyBonus={character.proficiencyBonus} onBonusChange={proficiencyBonus => setCharacter(current => current && ({ ...current, proficiencyBonus }))}
                                heroicInspiration={character.heroicInspiration} onInspirationChange={heroicInspiration => setCharacter(current => current && ({ ...current, heroicInspiration }))} />
                        </div>
                    )}

                    {activeTab === 'feats' && (
                        <div role="tabpanel" aria-label="Feats and features">
                            <Feats characterId={character.id} feats={character.feats} onChange={feats => setCharacter(current => current && ({ ...current, feats }))} />
                        </div>
                    )}

                    {activeTab === 'inventory' && (
                        <div role="tabpanel" aria-label="Equipment">
                        <Equipment
                            characterId={character.id}
                            weapons={character.weapons}
                            armor={[]}
                            magicItems={[]}
                            miscellaneous={character.items}
                            currency={character.currency}
                            treasure={character.treasure}
                            maxCarryWeight={(character.abilities.scores.strength ?? 10) * 15}
                        />
                        </div>
                    )}

                    {activeTab === 'spells' && (
                        <div role="tabpanel" aria-label="Spells">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <strong>Wizard</strong>
                                    <br />
                                    Spellcasting ability: INT
                                    <br />
                                    Spellcasting modifier: +3
                                    <br />
                                    Spell save DC: 13
                                    <br />
                                    Spell attack modifier: +5
                                </div>

                                <div className="col-md-9">
                                    <SpellSlots
                                        spellSlots={[
                                            {level: 1, total: 4, spent: 0},
                                            {level: 2, total: 3, spent: 0},
                                            {level: 3, total: 3, spent: 0},
                                            {level: 4, total: 3, spent: 0},
                                            {level: 5, total: 3, spent: 0},
                                            {level: 6, total: 2, spent: 0},
                                            {level: 7, total: 2, spent: 0},
                                            {level: 8, total: 1, spent: 0},
                                            {level: 9, total: 1, spent: 0},
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <nav className="sheet-navigation" aria-label="Character sheet sections">
                    {[
                        ['main', 'Stats', 'fa-user'],
                        ['spells', 'Spells', 'fa-book-open'],
                        ['inventory', 'Inventory', 'fa-sack-dollar'],
                        ['feats', 'Features', 'fa-scroll'],
                    ].map(([id, label, icon]) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setActiveTab(id)}
                            aria-current={activeTab === id ? 'page' : undefined}
                            className={activeTab === id ? 'is-active' : ''}
                        >
                            <i className={`fa-solid ${icon}`} aria-hidden="true" />
                            {label}
                        </button>
                    ))}
                </nav>
            </main>
        </Layout>
    );
}

export default CharacterSheet;
